import React, { useEffect } from 'react'

import AddIcon from '@mui/icons-material/Add'
import { TableHead as MuiTableHead, Typography, TableCell } from '@mui/material'
import Chip from '@mui/material/Chip'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import dayjs from 'dayjs'
import InfiniteScroll from 'react-infinite-scroll-component'
import {
  DiscrepancyStatus,
  InternalWorkItem,
  MaintenanceItem,
} from 'types/graphql'

import ApiError from 'src/components/ApiError'
import Loading from 'src/components/Loading'
import TypeAndDescriptionDisplay from 'src/components/TypeAndDescriptionDisplay'
import useDiscrepanciesTabData from 'src/hooks/requests/useDiscrepanciesTabData'
import AddDiscrepancySlider from 'src/components/Discrepanies/AddDiscrepancySlider'
import Button from 'src/components/MUI/Button'
import useHasPermission from 'src/hooks/useHasPermission'
import { Permissions } from '@wingwork/common/src/constants/permissions'
import { useSelector } from 'src/hooks/useSelector'

interface NonRoutineTasksTableProps {
  internalWorkItem: InternalWorkItem
  aircraftUsageLogId: string
  refreshWorkOrder: () => void
}

const DiscrepanciesTable: React.FC<NonRoutineTasksTableProps> = ({
  internalWorkItem,
  aircraftUsageLogId,
  refreshWorkOrder,
}) => {
  const [showAddDiscrepancyModal, setShowAddDiscrepancyModal] =
    React.useState(false)
  const [clickedTask, setClickedTask] =
    React.useState<MaintenanceItem>(undefined)
  const [readyForReview, setReadyForReview] = React.useState(true)
  const [refreshTable, setRefreshTable] = React.useState(0)

  const {
    items,
    hasNextPage,
    initialLoading,
    error,
    loadItems,
    fetchNextPage,
  } = useDiscrepanciesTabData(true)

  useEffect(() => {
    if (internalWorkItem) {
      loadItems(
        {
          discrepancyWorkItemId: internalWorkItem.id,
          unscheduledType: ['DISCREPANCY', 'MEL', 'NEF', 'WATCH_LIST'],
        },
        true
      )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalWorkItem, refreshTable])

  const getChipColor = (status: DiscrepancyStatus) => {
    switch (status) {
      case 'OPEN':
        return 'error'
      case 'CLOSED':
        return 'success'
      default:
        return 'default'
    }
  }

  const getDiscrepancyChips = (row: MaintenanceItem) => {
    const chips: string[] = [row.adSbType as string]
    if (row.tags.length) {
      row.tags.forEach((tag) => chips.push(tag.name))
    }
    return chips
  }

  const getNextDueValue = (row: MaintenanceItem) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nextDueValue: any = row.maintenanceNextDue[0]?.nextDueValue ?? {}
    if (nextDueValue?.date) {
      return dayjs(nextDueValue.date).format('MM/DD/YYYY')
    }
    return 'N/A'
  }

  const aircraftId = useSelector((state) => state.auditLog.aircraftId)
  const canCreateMtxItem = useHasPermission(
    Permissions.maintenanceItem.create,
    aircraftId
  )

  if (initialLoading) return <Loading />

  if (error) return <ApiError />

  return (
    <div className="p-3">
      <div>
        <Typography variant="subtitle1" fontWeight="bold">
          Discrepancies
        </Typography>
      </div>
      <TableContainer className="mt-1">
        {aircraftUsageLogId ? (
          <InfiniteScroll
            dataLength={items.length}
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={<Loading />}
            endMessage={
              <div className="flex h-full w-full flex-col items-center justify-center gap-2">
                <p>All Items Loaded.</p>
              </div>
            }
            style={{ overflowY: 'clip' }}
            scrollableTarget="main-content"
            scrollThreshold={0.65}
          >
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={'small'}
            >
              <TableHead />
              <TableBody>
                {items.map((row) => {
                  if (row.discrepancyStatus === 'OPEN' && readyForReview) {
                    setReadyForReview(false)
                  }
                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        hover
                        tabIndex={-1}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setClickedTask(row)
                          setShowAddDiscrepancyModal(true)
                        }}
                      >
                        <TableCell>
                          <Typography>{row.title}</Typography>
                        </TableCell>
                        <TableCell sx={{ position: 'relative' }}>
                          <TypeAndDescriptionDisplay
                            description={row.description}
                            chips={getDiscrepancyChips(row)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography>{getNextDueValue(row)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.discrepancyStatus}
                            color={getChipColor(row.discrepancyStatus)}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </InfiniteScroll>
        ) : (
          <Typography variant="body1">
            Apply times first to add discrepancies.
          </Typography>
        )}
      </TableContainer>
      <Button
        variant="outlined"
        color="base"
        startIcon={<AddIcon />}
        size="small"
        className="mt-2"
        disabled={!aircraftUsageLogId}
        onClick={() => {
          setClickedTask(undefined)
          setShowAddDiscrepancyModal(true)
        }}
        locked={!canCreateMtxItem}
        lockedTooltip="You do not have permission"
      >
        Add Discrepancy
      </Button>

      <AddDiscrepancySlider
        open={showAddDiscrepancyModal}
        onClose={(refresh) => {
          setClickedTask(undefined)
          setShowAddDiscrepancyModal(false)
          if (refresh) {
            refreshWorkOrder()
            setRefreshTable(refreshTable + 1)
          }
        }}
        isWorkOrder
        complianceOrWorkOrder={internalWorkItem}
        selectedTask={clickedTask}
        aircraftUsageLogId={aircraftUsageLogId}
      />
    </div>
  )
}

interface HeadCell {
  disablePadding: boolean
  id: string
  label: string
}

const internalWorkOrderHeadCells: readonly HeadCell[] = [
  {
    id: 'title',
    disablePadding: false,
    label: 'TITLE',
  },
  {
    id: 'typeDescription',
    disablePadding: false,
    label: 'TYPE, DESCRIPTION',
  },
  {
    id: 'nextDue',
    disablePadding: false,
    label: 'NEXT DUE',
  },
  {
    id: 'status',
    disablePadding: false,
    label: 'STATUS',
  },
]

const TableHead: React.FC = () => {
  return (
    <MuiTableHead>
      <TableRow>
        {internalWorkOrderHeadCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.id === 'actions' ? 'right' : 'left'}
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </MuiTableHead>
  )
}

export default DiscrepanciesTable
